import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock, Edit, CheckCircle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import AutoTranslatedText from '../AutoTranslatedText';

const SellerOverviewTab = ({ activeBooth, events, TIER_BENEFITS, openUpgradeModal, openPaymentForBooth }) => {
  const { t } = useTranslation();
  const [showBusinessHoursModal, setShowBusinessHoursModal] = useState(false);
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [businessHoursForm, setBusinessHoursForm] = useState({
    timezone: 'America/New_York',
    monday: { open: '09:00', close: '17:00', enabled: true },
    tuesday: { open: '09:00', close: '17:00', enabled: true },
    wednesday: { open: '09:00', close: '17:00', enabled: true },
    thursday: { open: '09:00', close: '17:00', enabled: true },
    friday: { open: '09:00', close: '17:00', enabled: true },
    saturday: { open: '', close: '', enabled: false },
    sunday: { open: '', close: '', enabled: false },
  });

  const openBusinessHoursModal = (booth) => {
    setSelectedBooth(booth);
    if (booth.business_hours) {
      const form = {
        timezone: booth.timezone || 'America/New_York',
        monday: { open: booth.business_hours.monday?.open || '', close: booth.business_hours.monday?.close || '', enabled: !!booth.business_hours.monday },
        tuesday: { open: booth.business_hours.tuesday?.open || '', close: booth.business_hours.tuesday?.close || '', enabled: !!booth.business_hours.tuesday },
        wednesday: { open: booth.business_hours.wednesday?.open || '', close: booth.business_hours.wednesday?.close || '', enabled: !!booth.business_hours.wednesday },
        thursday: { open: booth.business_hours.thursday?.open || '', close: booth.business_hours.thursday?.close || '', enabled: !!booth.business_hours.thursday },
        friday: { open: booth.business_hours.friday?.open || '', close: booth.business_hours.friday?.close || '', enabled: !!booth.business_hours.friday },
        saturday: { open: booth.business_hours.saturday?.open || '', close: booth.business_hours.saturday?.close || '', enabled: !!booth.business_hours.saturday },
        sunday: { open: booth.business_hours.sunday?.open || '', close: booth.business_hours.sunday?.close || '', enabled: !!booth.business_hours.sunday },
      };
      setBusinessHoursForm(form);
    }
    setShowBusinessHoursModal(true);
  };

  const saveBusinessHours = async () => {
    if (!selectedBooth) return;
    try {
      const businessHours = {};
      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].forEach(day => {
        if (businessHoursForm[day]?.enabled && businessHoursForm[day]?.open && businessHoursForm[day]?.close) {
          businessHours[day] = { open: businessHoursForm[day].open, close: businessHoursForm[day].close };
        }
      });
      await axios.put(`${API}/booths/${selectedBooth.id}/business-hours?timezone=${businessHoursForm.timezone}`, businessHours);
      toast.success('Business hours updated');
      setShowBusinessHoursModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update business hours');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.booth_info')}</CardTitle>
          <CardDescription>{t('dashboard.booth_info_desc')} {events.find(e => e.id === activeBooth?.event_id)?.name || t('dashboard.current_exhibit')}</CardDescription>
        </CardHeader>
        <CardContent>
          {activeBooth && (
            <div key={activeBooth.id} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 text-xs">{t('dashboard.company_name')}</Label>
                  <p className="font-medium">{activeBooth.company_name}</p>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">{t('dashboard.tier')}</Label>
                  <Badge className={TIER_BENEFITS[activeBooth.tier]?.color}>{t(TIER_BENEFITS[activeBooth.tier]?.nameKey)}</Badge>
                </div>
                <div>
                  <Label className="text-slate-500 text-xs">{t('dashboard.payment_status')}</Label>
                  <div className="flex items-center gap-2">
                    {activeBooth.payment_status === 'completed' ? (
                      <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> {t('dashboard.completed')}</Badge>
                    ) : (
                      <>
                        <Badge className="bg-amber-100 text-amber-700"><CreditCard className="h-3 w-3 mr-1" /> {t('dashboard.pending')}</Badge>
                        <Button size="sm" variant="outline" onClick={() => openPaymentForBooth(activeBooth)}>{t('dashboard.proceed_to_payment')}</Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-500 text-xs">{t('dashboard.description')}</Label>
                  <p className="text-sm">{activeBooth.description ? <AutoTranslatedText text={activeBooth.description} /> : t('dashboard.no_events')}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-500 text-xs">{t('dashboard.slogan')}</Label>
                  <p className="text-sm italic">{activeBooth.slogan ? <AutoTranslatedText text={activeBooth.slogan} /> : '-'}</p>
                </div>
              </div>
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium">{t('dashboard.tier_benefits')}:</h4>
                  {activeBooth.tier !== 'elite' && (
                    <Button size="sm" variant="outline" onClick={openUpgradeModal}>
                      <CreditCard className="h-3 w-3 mr-1" /> {t('dashboard.upgrade_tier')}
                    </Button>
                  )}
                </div>
                <ul className="list-disc list-inside text-sm text-slate-600">
                  {TIER_BENEFITS[activeBooth.tier]?.benefitKeys.map((key, i) => <li key={i}>{t(key)}</li>)}
                </ul>
              </div>
              <div className="border-t pt-4 mt-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center gap-2"><Clock className="h-4 w-4" /> {t('dashboard.business_hours')}</h4>
                  <Button size="sm" variant="outline" onClick={() => openBusinessHoursModal(activeBooth)}>
                    <Edit className="h-3 w-3 mr-1" /> {t('common.edit') || 'Configure'}
                  </Button>
                </div>
                <p className="text-sm text-slate-500 mb-2">{t('dashboard.operating_hours_desc')}</p>
                {activeBooth.business_hours ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                      <div key={day} className="bg-slate-50 rounded px-2 py-1">
                        <span className="font-medium capitalize">{day.slice(0, 3)}: </span>
                        {activeBooth.business_hours[day]?.open && activeBooth.business_hours[day]?.close
                          ? <span>{activeBooth.business_hours[day].open} - {activeBooth.business_hours[day].close}</span>
                          : <span className="text-slate-400">{t('common.close') || 'Closed'}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">{t('dashboard.set_business_hours')}</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Business Hours Modal */}
      <Dialog open={showBusinessHoursModal} onOpenChange={setShowBusinessHoursModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5" /> Configure Business Hours</DialogTitle>
            <DialogDescription>Set your booth's availability for video calls.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Timezone</Label>
              <Select value={businessHoursForm.timezone} onValueChange={(val) => setBusinessHoursForm({...businessHoursForm, timezone: val})}>
                <SelectTrigger><SelectValue placeholder="Select timezone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                  <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                  <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                  <SelectItem value="America/Anchorage">Alaska Time (AKT)</SelectItem>
                  <SelectItem value="Pacific/Honolulu">Hawaii Time (HT)</SelectItem>
                  <SelectItem value="Europe/London">London (GMT/BST)</SelectItem>
                  <SelectItem value="Europe/Paris">Paris (CET/CEST)</SelectItem>
                  <SelectItem value="Asia/Tokyo">Tokyo (JST)</SelectItem>
                  <SelectItem value="Asia/Shanghai">Shanghai (CST)</SelectItem>
                  <SelectItem value="Australia/Sydney">Sydney (AEST)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label>Hours by Day</Label>
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                <div key={day} className="flex items-center gap-3">
                  <div className="w-24">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={businessHoursForm[day]?.enabled}
                        onChange={(e) => setBusinessHoursForm({ ...businessHoursForm, [day]: { ...businessHoursForm[day], enabled: e.target.checked } })}
                        className="rounded" />
                      <span className="capitalize text-sm font-medium">{day.slice(0, 3)}</span>
                    </label>
                  </div>
                  {businessHoursForm[day]?.enabled ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input type="time" value={businessHoursForm[day]?.open || '09:00'}
                        onChange={(e) => setBusinessHoursForm({ ...businessHoursForm, [day]: { ...businessHoursForm[day], open: e.target.value } })} className="w-28" />
                      <span className="text-slate-400">to</span>
                      <Input type="time" value={businessHoursForm[day]?.close || '17:00'}
                        onChange={(e) => setBusinessHoursForm({ ...businessHoursForm, [day]: { ...businessHoursForm[day], close: e.target.value } })} className="w-28" />
                    </div>
                  ) : <span className="text-sm text-slate-400 italic">Closed</span>}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBusinessHoursModal(false)}>Cancel</Button>
            <Button onClick={saveBusinessHours}>Save Hours</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SellerOverviewTab;
